import { useCallback, useState } from 'react';
import type { ActivityDeletionBatchView } from '@internal/spassu-code-contract';
import type { SpassuCodeApi } from '../../api';
import type { PortfolioSelection } from './usePortfolioSelection';
import { useDeletionBatchTracking } from './useDeletionBatchTracking';
import type { usePortfolioQueries } from './usePortfolioQueries';

function deletionSummary(view: ActivityDeletionBatchView) {
  const deleted = view.items.filter(item => item.outcome === 'deleted').length;
  const pending = view.items.filter(
    item => item.outcome === 'cleanup_pending',
  ).length;
  return `${deleted} excluída(s); ${pending} aguardando o agente local.`;
}

function useDeletionFeedback() {
  const [actionError, setActionError] = useState<Error>();
  const [actionInfo, setActionInfo] = useState<string>();
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setActionError(undefined);
    try {
      await action();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };
  return { actionError, setActionError, actionInfo, setActionInfo, busy, run };
}

type DeletionContext = {
  api: SpassuCodeApi;
  selection: PortfolioSelection;
  feedback: ReturnType<typeof useDeletionFeedback>;
  tracking: ReturnType<typeof useDeletionBatchTracking>;
  refresh: () => Promise<unknown>;
};

function requestBatch({
  api,
  selection,
  feedback,
  tracking,
  refresh,
}: DeletionContext) {
  if (selection.selectedIds.length === 0) return Promise.resolve();
  return feedback.run(async () => {
    feedback.setActionInfo(undefined);
    const view = await api.requestActivityDeletionBatch(
      selection.selectedIds,
      `batch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    );
    tracking.trackBatch(view);
    selection.clear();
    feedback.setActionInfo(deletionSummary(view));
    await refresh();
  });
}

function overrideCleanup(context: DeletionContext, activityId: string) {
  const { api, feedback, tracking, refresh } = context;
  return feedback.run(async () => {
    await api.overrideActivityDeletionCleanup(
      activityId,
      `override-${activityId}-${Date.now()}`,
    );
    feedback.setActionInfo(
      'Exclusão local ignorada; dados centrais removidos.',
    );
    await tracking.refreshBatch();
    await refresh();
  });
}

export function usePortfolioDeletion(
  api: SpassuCodeApi,
  selection: PortfolioSelection,
  queries: ReturnType<typeof usePortfolioQueries>,
) {
  const feedback = useDeletionFeedback();
  const { setActionError } = feedback;
  const { loadActivities: refreshActivities, loadFilterOptions } = queries;
  const loadActivities = useCallback(() => {
    setActionError(undefined);
    return refreshActivities();
  }, [refreshActivities, setActionError]);
  const tracking = useDeletionBatchTracking(api, loadActivities);
  const refresh = () => Promise.all([loadActivities(), loadFilterOptions()]);
  const context = { api, selection, feedback, tracking, refresh };
  return {
    actionError: feedback.actionError,
    actionInfo: feedback.actionInfo,
    busy: feedback.busy,
    batch: tracking.deletionBatch,
    loadActivities,
    requestBatch: () => requestBatch(context),
    overrideCleanup: (id: string) => overrideCleanup(context, id),
  };
}
export type PortfolioDeletion = ReturnType<typeof usePortfolioDeletion>;
